import {
  Button,
  Input,
  InputType,
  Typography,
} from '../../components/primitives';
import Alert, { AlertProps } from '../../components/ui/Alert';
import ContactSupport from '../../components/ui/ContactSupport';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Icons } from '../../icons';
import { SearchParamsBuilder, testId, toParams } from '../../utils';
import { PageCard } from './PageCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation, Trans } from '@rpi/i18n';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export const ForgotPasswordRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });
  const email = React.useMemo(
    () => searchParams.get('email') ?? '',
    [searchParams],
  );
  const ForgotPasswordSchema = z
    .object({
      email: z.string().email(`${t('email_requirements')}`),
    })
    .required();

  const form = useForm<z.infer<typeof ForgotPasswordSchema>>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email,
    },
  });

  const navigate = useNavigate();

  const mutation = Cornerstone.useForgotPassword({
    mutation: {
      onSuccess() {
        navigate(
          toParams(
            '/auth/forgot-password/email-sent',
            new SearchParamsBuilder(searchParams)
              .filter((k) => k === 'redirect')
              .add('email', form.getValues().email),
          ),
        );
      },
      onError() {},
    },
  });

  const alertProps = useAlertProps(mutation);

  function onSubmit(data: z.infer<typeof ForgotPasswordSchema>) {
    mutation.mutate({
      data: {
        email: data.email,
        redirectUrl: searchParams.get('redirect') ?? undefined,
      },
    });
  }

  return (
    <PageCard>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {alertProps && <Alert {...alertProps} />}
          <Typography variant="title-default" className="text-center">
            {t('forgot_password_title')}
          </Typography>
          <Typography variant="body" className="text-center">
            {t('forgot_password')}
          </Typography>
          <RequiredFieldIndicator />
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('email', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.email"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('email')}
                    type={InputType.EMAIL}
                    placeholder=""
                    {...field}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('email', 'message')} />
              </FormItem>
            )}
          />
          <Button
            {...testId('submit')}
            type="submit"
            loading={mutation.isLoading}
          >
            {t('forgot_password_reset')}
          </Button>
          <ContactSupport />
        </form>
      </Form>
    </PageCard>
  );
};

function useAlertProps(
  mutation: ReturnType<typeof Cornerstone.useForgotPassword>,
): AlertProps | null {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });
  if (!mutation.error) {
    return null;
  }
  const error = mutation.error as any;
  if (error?.response?.data?.errorCode === 'NOT_FOUND') {
    return {
      ...testId('error'),
      variant: 'error',
      children: t('forgot_password_error_not_found'),
    };
  }
  return {
    ...testId('error'),
    variant: 'error',
    children: t('generic_error'),
  };
}
